import React, { useState, useEffect } from "react";
import { View, Text, TextInput, Pressable, ScrollView, StyleSheet, Dimensions } from "react-native";
import { getFirestore, doc, getDoc, updateDoc, query, collection, where, getDocs } from "firebase/firestore";
import { useUser } from "./UserContext";

const { width } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;

const fontFamilies = {
	Bodoni72Smallcaps: "Bodoni72Smallcaps",
};

// Data Model (stored in card object):
// {
//   question: string,
//   answers: [{ id: string, text: string, isCorrect: boolean }],
//   votes: [{ userId: string, answerId: string, timestamp: Date }]
// }

const Poll = ({ card, isOwner, widgetIndex, pageOwnerId, setHasChanges, isPreviewMode = false }) => {
	const db = getFirestore();
	const { currentUser, currentUserData } = useUser();

	// Owner editing state
	const [isEditing, setIsEditing] = useState(false);
	const [question, setQuestion] = useState(card?.question || "");
	const [answers, setAnswers] = useState(card?.answers || [
		{ id: "1", text: "", isCorrect: false },
		{ id: "2", text: "", isCorrect: false }
	]);

	// Visitor state
	const [votes, setVotes] = useState(card?.votes || []);
	const [userVote, setUserVote] = useState(null);
	const [showResults, setShowResults] = useState(false);

	const getUserId = () => {
		if (currentUserData?.uid) return currentUserData.uid;
		let sessionId = localStorage.getItem('pollSessionId');
		if (!sessionId) {
			sessionId = 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
			localStorage.setItem('pollSessionId', sessionId);
		}
		return sessionId;
	};

	useEffect(() => {
		const userId = getUserId();
		const existingVote = votes.find(v => v.userId === userId);
		if (existingVote) {
			setUserVote(existingVote.answerId);
			setShowResults(true);
		}
	}, [votes]);

	useEffect(() => {
		if (card) {
			setQuestion(card.question || "");
			setAnswers(card.answers || []);
			setVotes(card.votes || []);
		}
		
		// Also load fresh data from Firestore to ensure we have latest votes
		loadLatestPollData();
	}, [card]);

	const loadLatestPollData = async () => {
		if (!pageOwnerId) return;
		
		try {
			const q = query(collection(db, "users"), where("uid", "==", pageOwnerId));
			const querySnapshot = await getDocs(q);

			if (!querySnapshot.empty) {
				const docSnap = querySnapshot.docs[0];
				const userData = docSnap.data();
				const cards = userData.pages?.eventsPageConfig?.cards || [];
				const currentCard = cards[widgetIndex];
				
				if (currentCard && currentCard.votes) {
					
					setVotes(currentCard.votes);
				}
			}
		} catch (error) {
			
		}
	};

	const savePollData = async () => {
		if (!isOwner || !pageOwnerId) return;
		if (isPreviewMode) {
			setIsEditing(false);
			if (setHasChanges) setHasChanges(false);
			return;
		}

		
		const q = query(collection(db, "users"), where("uid", "==", pageOwnerId));
		const querySnapshot = await getDocs(q);

		if (!querySnapshot.empty) {
			querySnapshot.forEach(async (docSnapshot) => {
				const userDocRef = doc(db, "users", docSnapshot.id);
				const docData = await getDoc(userDocRef);
				
				if (docData.exists()) {
					const existingData = docData.data();
					const existingCards = existingData.pages?.eventsPageConfig?.cards || [];
					const updatedCards = [...existingCards];
					
					// Get existing votes to preserve them
					const existingVotes = existingCards[widgetIndex]?.votes || [];
					
					updatedCards[widgetIndex] = {
						...updatedCards[widgetIndex],
						type: 'Poll', // Explicitly preserve the type
						question: question.trim(),
						answers: answers.filter(a => a.text.trim()),
						votes: existingVotes // Preserve existing votes
					};

					await updateDoc(userDocRef, {
						"pages.eventsPageConfig.cards": updatedCards
					});
					
					
					if (setHasChanges) setHasChanges(false);
				}
			});
		}
		setIsEditing(false);
	};

const handleAddAnswer = () => {
	if (answers.length < 5) {
		setAnswers([...answers, { id: Date.now().toString(), text: "", isCorrect: false }]);
	}
};

const handleRemoveAnswer = (id) => {
	if (answers.length > 2) {
		setAnswers(answers.filter(a => a.id !== id));
	}
};

const handleAnswerTextChange = (id, text) => {
	setAnswers(answers.map(a => a.id === id ? { ...a, text } : a));
};

const toggleCorrectAnswer = (id) => {
		setAnswers(answers.map(a => a.id === id ? { ...a, isCorrect: !a.isCorrect } : a));
	};

	const handleSubmitVote = async (answerId) => {
		if (!answerId || userVote) return;

		
		const userId = getUserId();
		
		// Format name as "First Name Last Initial" for privacy
		const formatUserName = () => {
			if (currentUserData?.firstName) {
				const firstName = currentUserData.firstName;
				const lastInitial = currentUserData.lastName ? currentUserData.lastName.charAt(0).toUpperCase() + '.' : '';
				return `${firstName} ${lastInitial}`.trim();
			}
			return null;
		};
		
		const newVote = {
			userId,
			userName: formatUserName(),
			userEmail: currentUserData?.email || null,
			answerId,
			timestamp: new Date()
		};

		const updatedVotes = [...votes, newVote];
		

		setVotes(updatedVotes);
		setUserVote(answerId);
		setShowResults(true);

		if (isPreviewMode) return;

		// Persist to Firestore
		try {
			const q = query(collection(db, "users"), where("uid", "==", pageOwnerId));
			const querySnapshot = await getDocs(q);

			if (!querySnapshot.empty) {
				querySnapshot.forEach(async (docSnapshot) => {
					const userDocRef = doc(db, "users", docSnapshot.id);
					const docData = await getDoc(userDocRef);
					
					if (docData.exists()) {
						const existingData = docData.data();
						const existingCards = existingData.pages?.eventsPageConfig?.cards || [];
						const updatedCards = [...existingCards];
						updatedCards[widgetIndex] = {
							...updatedCards[widgetIndex],
							type: 'Poll', // Explicitly preserve the type
							votes: updatedVotes
						};

						await updateDoc(userDocRef, {
							"pages.eventsPageConfig.cards": updatedCards
						});
						
						
					}
				});
			}
		} catch (error) {
			
		}
	};

	const getTotalVotes = () => votes.length;

	const getVotesForAnswer = (answerId) => {
		return votes.filter(v => v.answerId === answerId).length;
	};

	const getPercentage = (answerId) => {
		const total = getTotalVotes();
		const answerVotes = getVotesForAnswer(answerId);
		return total > 0 ? Math.round((answerVotes / total) * 100) : 0;
	};

	// Owner View
	if (isOwner && isEditing) {
		return (
			<View style={styles.container}>
				<Text style={styles.title}>Edit Poll</Text>
				
				<TextInput
					style={styles.questionInput}
					placeholder="Enter your question"
					value={question}
					onChangeText={setQuestion}
					multiline
				/>

				<Text style={styles.sectionTitle}>Answers (2-5 required)</Text>
				{answers.map((answer, index) => (
					<View key={answer.id} style={styles.answerRow}>
						<TextInput
							style={styles.answerInput}
							placeholder={`Answer ${index + 1}`}
							value={answer.text}
							onChangeText={(text) => handleAnswerTextChange(answer.id, text)}
						/>
						<Pressable 
							onPress={() => toggleCorrectAnswer(answer.id)}
							style={styles.correctButton}
						>
							<Text style={styles.correctButtonText}>
								{answer.isCorrect ? "✓" : "○"}
							</Text>
						</Pressable>
						{answers.length > 2 && (
							<Pressable onPress={() => handleRemoveAnswer(answer.id)} style={styles.removeButton}>
								<Text style={styles.removeButtonText}>×</Text>
							</Pressable>
						)}
					</View>
				))}

				{answers.length < 5 && (
					<Pressable onPress={handleAddAnswer} style={styles.addButton}>
						<Text style={styles.addButtonText}>+ Add Answer</Text>
					</Pressable>
				)}

				<Text style={styles.hint}>Tap ○ to mark correct answer (optional)</Text>

				{votes.length > 0 && (
					<View style={styles.voteWarning}>
						<Text style={styles.voteWarningText}>
							⚠️ This poll has {votes.length} vote{votes.length === 1 ? '' : 's'}
						</Text>
						<Pressable 
							onPress={async () => {
								if (window.confirm(`Clear all ${votes.length} vote${votes.length === 1 ? '' : 's'}? This cannot be undone.`)) {
									setVotes([]);
									if (isPreviewMode) return;

									// Persist cleared votes to Firestore
									try {
										const q = query(collection(db, "users"), where("uid", "==", pageOwnerId));
										const querySnapshot = await getDocs(q);
										
										if (!querySnapshot.empty) {
											querySnapshot.forEach(async (docSnapshot) => {
												const userDocRef = doc(db, "users", docSnapshot.id);
												const docData = await getDoc(userDocRef);
												
												if (docData.exists()) {
													const existingData = docData.data();
													const existingCards = existingData.pages?.eventsPageConfig?.cards || [];
													const updatedCards = [...existingCards];
													updatedCards[widgetIndex] = {
														...updatedCards[widgetIndex],
														type: 'Poll',
														votes: []
													};
													
													await updateDoc(userDocRef, {
														"pages.eventsPageConfig.cards": updatedCards
													});
													
													
												}
											});
										}
									} catch (error) {
										
									}
								}
							}} 
							style={styles.clearVotesButton}
						>
							<Text style={styles.clearVotesButtonText}>Clear All Votes</Text>
						</Pressable>
					</View>
				)}

				<View style={styles.actionButtons}>
					<Pressable onPress={savePollData} style={styles.saveButton}>
						<Text style={styles.saveButtonText}>Save</Text>
					</Pressable>
					<Pressable onPress={() => setIsEditing(false)} style={styles.cancelButton}>
						<Text style={styles.cancelButtonText}>Cancel</Text>
					</Pressable>
				</View>
			</View>
		);
	}

	// Owner View - Display Mode
	if (isOwner && !isEditing) {
		return (
			<View style={styles.container}>
				<View style={styles.header}>
					<Text style={styles.title}>{question || "No poll created yet"}</Text>
					<Pressable onPress={() => setIsEditing(true)} style={styles.editButton}>
						<Text style={styles.editButtonText}>Edit</Text>
					</Pressable>
				</View>
				
				{question && (
					<>
						<Text style={styles.responsesCount}>{getTotalVotes()} responses</Text>
						{answers.map(answer => (
							<View key={answer.id} style={styles.resultRow}>
								<Text style={styles.answerText}>{answer.text}</Text>
								<View style={styles.barContainer}>
									<View style={[styles.bar, { width: `${getPercentage(answer.id)}%` }]} />
									<Text style={styles.percentage}>{getPercentage(answer.id)}%</Text>
								</View>
							</View>
						))}
					</>
				)}
			</View>
		);
	}

	// Visitor View - Before Submission
	if (!showResults && question) {
		return (
			<View style={styles.container}>
				<Text style={styles.title}>{question}</Text>
				
				{answers.filter(a => a.text.trim()).map(answer => (
					<Pressable 
						key={answer.id} 
						onPress={() => handleSubmitVote(answer.id)}
						style={styles.answerOption}
					>
						<Text style={styles.answerOptionText}>{answer.text}</Text>
					</Pressable>
				))}
			</View>
		);
	}

	// Visitor View - After Submission
	if (showResults && question) {
		return (
			<View style={styles.container}>
				<Text style={styles.title}>{question}</Text>
				<Text style={styles.thankYou}>Thank you for voting!</Text>
				
				{answers.map(answer => {
					const isUserChoice = answer.id === userVote;
					// Don't show correct answer to visitors - keep it hidden for owner/admin only
					
					return (
						<View key={answer.id} style={styles.resultRow}>
							<View style={styles.answerHeader}>
								<Text style={[styles.answerText, isUserChoice && styles.userChoice]}>
									{answer.text} {isUserChoice && "(Your vote)"}
								</Text>
							</View>
							<View style={styles.barContainer}>
								<View style={[styles.bar, { width: `${getPercentage(answer.id)}%` }]} />
								<Text style={styles.percentage}>{getPercentage(answer.id)}%</Text>
							</View>
						</View>
					);
				})}
				
				<Text style={styles.totalVotes}>{getTotalVotes()} total votes</Text>
			</View>
		);
	}

	// Empty state
	return (
		<View style={styles.container}>
			<Text style={styles.emptyText}>No poll available</Text>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: "#fefde2",
		padding: isMobile ? 15 : 20,
		borderRadius: 25,
		width: isMobile ? '100%' : 400,
		minHeight: 300,
	},
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginBottom: 15,
	},
	title: {
		fontFamily: fontFamilies.Bodoni72Smallcaps,
		fontSize: isMobile ? 20 : 24,
		color: "#4c956c",
		marginBottom: 15,
		flex: 1,
	},
	questionInput: {
		borderWidth: 1,
		borderColor: "#ddd",
		borderRadius: 10,
		padding: 12,
		fontSize: 16,
		backgroundColor: "#fff",
		marginBottom: 20,
		minHeight: 60,
	},
	sectionTitle: {
		fontSize: 14,
		fontWeight: "600",
		color: "#666",
		marginBottom: 10,
	},
	answerRow: {
		flexDirection: 'row',
		alignItems: 'center',
		marginBottom: 10,
	},
	answerInput: {
		flex: 1,
		borderWidth: 1,
		borderColor: "#ddd",
		borderRadius: 8,
		padding: 10,
		fontSize: 14,
		backgroundColor: "#fff",
	},
	correctButton: {
		width: 36,
		height: 36,
		marginLeft: 8,
		backgroundColor: "#4c956c",
		borderRadius: 18,
		alignItems: 'center',
		justifyContent: 'center',
	},
	correctButtonText: {
		color: "#fff",
		fontSize: 18,
		fontWeight: "bold",
	},
	removeButton: {
		width: 36,
		height: 36,
		marginLeft: 8,
		backgroundColor: "#dc3545",
		borderRadius: 18,
		alignItems: 'center',
		justifyContent: 'center',
	},
	removeButtonText: {
		color: "#fff",
		fontSize: 24,
		lineHeight: 24,
	},
	addButton: {
		padding: 12,
		backgroundColor: "#e8e8e8",
		borderRadius: 8,
		alignItems: 'center',
		marginTop: 10,
	},
	addButtonText: {
		color: "#4c956c",
		fontSize: 14,
		fontWeight: "600",
	},
	hint: {
		fontSize: 12,
		color: "#888",
		marginTop: 10,
		fontStyle: 'italic',
	},
	actionButtons: {
		flexDirection: 'row',
		gap: 10,
		marginTop: 20,
	},
	saveButton: {
		flex: 1,
		backgroundColor: "#4c956c",
		padding: 14,
		borderRadius: 25,
		alignItems: 'center',
	},
	saveButtonText: {
		color: "#fff",
		fontSize: 16,
		fontWeight: "600",
	},
	cancelButton: {
		flex: 1,
		backgroundColor: "#e8e8e8",
		padding: 14,
		borderRadius: 25,
		alignItems: 'center',
	},
	cancelButtonText: {
		color: "#666",
		fontSize: 16,
		fontWeight: "600",
	},
	editButton: {
		backgroundColor: "#4c956c",
		paddingHorizontal: 16,
		paddingVertical: 8,
		borderRadius: 20,
	},
	editButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
	responsesCount: {
		fontSize: 13,
		color: "#888",
		marginBottom: 15,
	},
	answerOption: {
		backgroundColor: "#fff",
		padding: 16,
		borderRadius: 12,
		marginBottom: 10,
		borderWidth: 2,
		borderColor: "#ddd",
	},
	answerOptionText: {
		fontSize: 16,
		color: "#333",
		textAlign: 'center',
	},
	thankYou: {
		fontSize: 14,
		color: "#4c956c",
		marginBottom: 15,
		fontWeight: "600",
	},
	resultRow: {
		marginBottom: 16,
	},
	answerHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 6,
	},
	answerText: {
		fontSize: 14,
		color: "#333",
		fontWeight: "500",
		flex: 1,
	},
	userChoice: {
		color: "#4c956c",
		fontWeight: "700",
	},
	correctBadge: {
		backgroundColor: "#4c956c",
		color: "#fff",
		fontSize: 12,
		paddingHorizontal: 8,
		paddingVertical: 3,
		borderRadius: 10,
		fontWeight: "600",
	},
	barContainer: {
		position: 'relative',
		height: 28,
		backgroundColor: "#e8e8e8",
		borderRadius: 14,
		overflow: 'hidden',
	},
	bar: {
		position: 'absolute',
		left: 0,
		top: 0,
		bottom: 0,
		backgroundColor: "#4c956c",
		borderRadius: 14,
	},
	percentage: {
		position: 'absolute',
		right: 10,
		top: 0,
		bottom: 0,
		lineHeight: 28,
		fontSize: 13,
		color: "#333",
		fontWeight: "600",
	},
	totalVotes: {
		fontSize: 13,
		color: "#888",
		marginTop: 10,
		textAlign: 'center',
	},
	emptyText: {
		fontSize: 16,
		color: "#888",
		textAlign: 'center',
		marginTop: 40,
	},
	voteWarning: {
		backgroundColor: '#fff3cd',
		borderRadius: 10,
		padding: 12,
		marginBottom: 15,
		borderWidth: 1,
		borderColor: '#ffc107',
	},
	voteWarningText: {
		fontSize: 13,
		color: '#856404',
		marginBottom: 8,
		fontWeight: '600',
	},
	clearVotesButton: {
		backgroundColor: '#dc3545',
		padding: 10,
		borderRadius: 8,
		alignItems: 'center',
	},
	clearVotesButtonText: {
		color: '#fff',
		fontSize: 13,
		fontWeight: '600',
	},
});

export default Poll;
